import type { AuthUser } from "./roles";

/**
 * Тонкий клиент REST API АС «КИД» (SK-5).
 *
 * Пути всегда относительные (`/api/...`): в проде их проксирует Caddy, локально —
 * dev-прокси Vite (docs/05 §3.1, §8). Сессия — cookie `HttpOnly` (Р-5), поэтому
 * `credentials: "same-origin"` (клиент и api — один origin) и никаких токенов в JS.
 */

/** Результат входа для формы: успех с пользователем либо типизированная причина отказа. */
export type LoginResult =
  | { readonly ok: true; readonly user: AuthUser }
  | {
      readonly ok: false;
      readonly reason: "invalid_credentials" | "too_many_attempts" | "validation" | "network";
      readonly message: string;
      readonly retryAfterSec?: number;
    };

interface LoginSuccessBody {
  user: AuthUser;
}
interface ErrorBody {
  error?: string;
  message?: string;
  retryAfterSec?: number;
}

const JSON_HEADERS = { "Content-Type": "application/json", Accept: "application/json" } as const;

async function readJson<T>(res: Response): Promise<T | null> {
  try {
    return (await res.json()) as T;
  } catch {
    return null;
  }
}

/**
 * Текущий пользователь по cookie-сессии. `null` — не аутентифицирован (401) либо api
 * недоступен: в обоих случаях UI показывает вход, не «падает».
 */
export async function fetchCurrentUser(): Promise<AuthUser | null> {
  let res: Response;
  try {
    res = await fetch("/api/auth/me", {
      method: "GET",
      headers: { Accept: "application/json" },
      credentials: "same-origin",
    });
  } catch {
    return null;
  }
  if (!res.ok) return null;
  const body = await readJson<LoginSuccessBody>(res);
  return body?.user ?? null;
}

/** Вход по логину/паролю (POST /api/auth/login). Ошибки отображаются формой по `reason`. */
export async function login(loginValue: string, password: string): Promise<LoginResult> {
  let res: Response;
  try {
    res = await fetch("/api/auth/login", {
      method: "POST",
      headers: JSON_HEADERS,
      credentials: "same-origin",
      body: JSON.stringify({ login: loginValue, password }),
    });
  } catch {
    return { ok: false, reason: "network", message: "Сервер недоступен. Повторите позже." };
  }

  if (res.ok) {
    const body = await readJson<LoginSuccessBody>(res);
    if (body?.user) return { ok: true, user: body.user };
    return { ok: false, reason: "network", message: "Неожиданный ответ сервера." };
  }

  const body = (await readJson<ErrorBody>(res)) ?? {};
  if (res.status === 429) {
    return {
      ok: false,
      reason: "too_many_attempts",
      message: "Слишком много попыток входа. Повторите позже.",
      retryAfterSec: body.retryAfterSec,
    };
  }
  if (res.status === 400) {
    // Своё русское сообщение, а не сырой текст сервера (правило 8): на форме входа поля
    // обязательные, 400 почти недостижим, а транслировать чужой текст рискованно.
    return {
      ok: false,
      reason: "validation",
      message: "Проверьте введённые данные.",
    };
  }
  // 401 и всё прочее: причину не уточняем (сервер её намеренно скрывает, SK-4).
  return {
    ok: false,
    reason: "invalid_credentials",
    message: "Неверный логин или пароль.",
  };
}

/** Брендинг продукта из конфигурации инстанса (D-10): имя и фиксированный дескриптор. */
export interface ProductBranding {
  readonly name: string;
  readonly descriptor: string;
}

interface AppInfoBody {
  product?: Partial<ProductBranding>;
}

/**
 * Имя продукта и дескриптор (`GET /api/app-info`, D-10). Маршрут публичный: его читает
 * страница входа до аутентификации. `null` — api недоступен или ответил неожиданным телом;
 * интерфейс в этом случае просто не показывает брендинг (подставлять своё нельзя: на инстансе
 * заказчика это был бы чужой бренд).
 */
export async function fetchAppInfo(): Promise<ProductBranding | null> {
  let res: Response;
  try {
    res = await fetch("/api/app-info", {
      method: "GET",
      headers: { Accept: "application/json" },
      credentials: "same-origin",
    });
  } catch {
    return null;
  }
  if (!res.ok) return null;
  const body = await readJson<AppInfoBody>(res);
  const name = body?.product?.name?.trim();
  const descriptor = body?.product?.descriptor?.trim();
  return name && descriptor ? { name, descriptor } : null;
}

/** Результат смены своего пароля (POST /api/auth/change-password). */
export type ChangePasswordResult =
  { readonly ok: true } | { readonly ok: false; readonly message: string };

/**
 * Смена своего пароля (SK-4; экран появился в D-15). Сообщения об отказе сервер формирует
 * сам и по-русски — политика пароля живёт на сервере в одном месте (`passwordPolicy.ts`),
 * дублировать её текстами на клиенте нельзя: разойдутся.
 */
export async function changePassword(
  currentPassword: string,
  newPassword: string,
): Promise<ChangePasswordResult> {
  let res: Response;
  try {
    res = await fetch("/api/auth/change-password", {
      method: "POST",
      headers: JSON_HEADERS,
      credentials: "same-origin",
      body: JSON.stringify({ currentPassword, newPassword }),
    });
  } catch {
    return { ok: false, message: "Сервер недоступен. Повторите позже." };
  }

  if (res.ok) return { ok: true };

  const body = (await readJson<ErrorBody>(res)) ?? {};
  if (res.status === 429) {
    return { ok: false, message: "Слишком много попыток. Повторите позже." };
  }
  if (res.status === 403) {
    // Единственный 403 этого маршрута — неверный текущий пароль (`bad_current`): гард D-15
    // смену пароля не закрывает, иначе человек с флагом не мог бы её выполнить.
    return { ok: false, message: body.message ?? "Текущий пароль указан неверно." };
  }
  return { ok: false, message: body.message ?? "Не удалось сменить пароль." };
}

/** Выход (POST /api/auth/logout). Всегда завершается — контекст очистит сессию у себя. */
export async function logout(): Promise<void> {
  try {
    await fetch("/api/auth/logout", { method: "POST", credentials: "same-origin" });
  } catch {
    // Молча: даже при сетевой ошибке клиент забывает пользователя и уходит на вход.
  }
}
